import {
  CASH_CROP_HARVEST_TYPES,
  CASH_CROP_PLANTING_RATE_UNIT,
  CASH_CROP_SPECIES,
  JSONSchema7WithErrors,
  RDCashCrop,
  RDCashCropInput,
} from '@cibo/core'
import { applyDateTransforms } from '@cibo/ui'
import { propEq } from 'ramda'
import * as Yup from 'yup'
import { DetailEditorProps, ITraitFeatureByIdYear } from '../types'
import { isDetailRequired } from '../useIsRequired'
import { findInitialValuesDetail } from '../utils'
import { CashCropCell } from './CashCropCell'
import { CashCropCellEditor } from './CashCropCellEditor'
import { CashCropDetailSimpleCell } from './CashCropDetailSimpleCell'
import { CashCropEditor } from './CashCropEditor'
import { CashCropEditorSimple } from './CashCropEditorSimple'
import { CashCropResultsOnlyEditor } from './CashCropResultsOnlyEditor'
import { isFallow } from './isFallow'

const TRAIT_ID = 'cashCrop'

export const CashCropResult: ITraitFeatureByIdYear<RDCashCrop> = {
  rollups: {},
  forbidBulkEdit: false,
  traitId: TRAIT_ID,
  editor: CashCropResultsOnlyEditor,
  cellDisplay: CashCropCell,
  SimpleCellView: CashCropDetailSimpleCell,
  simpleEditor: CashCropEditorSimple,
  ns: '@cibo/landmanager/CashCropEditor',
  validationSchema: (props: Pick<DetailEditorProps, 'requirement'>) =>
    Yup.string().oneOf(CASH_CROP_SPECIES).nullable() as Yup.StringSchema<RDCashCrop['result']>,

  initialValues: ({ fieldModels, year }) => {
    const cashCrops =
      fieldModels.length === 1
        ? fieldModels[0].details.filter(propEq('traitId', 'cashCrop'))
        : undefined
    return cashCrops?.filter(d => d.year === year)?.[0]?.result
  },
  cellEditor: CashCropCellEditor,
  detailFromEditorValues: (formValues, requirement) => ({
    traitId: TRAIT_ID,
    year: requirement?.year,
    result: formValues.input,
  }),
}

export const CashCropInput: ITraitFeatureByIdYear<RDCashCrop> = {
  rollups: {},
  forbidBulkEdit: false,
  traitId: TRAIT_ID,
  editor: CashCropEditor,
  cellDisplay: CashCropCell,
  SimpleCellView: CashCropDetailSimpleCell,
  ns: '@cibo/landmanager/CashCropEditor',
  simpleEditor: CashCropEditorSimple,

  validJsonSchema: ({ requirement, t }) => {
    const requiredProps = [
      'plantedAt',
      'harvestedAt',
      'plantingRate',
      'plantingRateUnit',
      'herbicideBurndown',
      'fireBurndown',
    ].reduce<string[]>((acc, fieldName) => {
      if (isDetailRequired({ requirement, fieldName })) {
        acc.push(fieldName)
      }
      return acc
    }, [])

    return {
      type: 'object',
      properties: {
        crop: { enum: [...CASH_CROP_SPECIES, 'preventPlant'] },
        preventPlant: { type: 'boolean' },
        intendedCrop: { enum: [...CASH_CROP_SPECIES] },
        acres: { type: 'number', minimum: 0 },
        plantedAt: { type: ['null', 'string'], format: 'date', dateBeforeField: 'harvestedAt' },
        harvestedAt: { type: ['null', 'string'], format: 'date', dateAfterField: 'plantedAt' },
        harvestType: { enum: [...CASH_CROP_HARVEST_TYPES] },
        residuePercentageRemoved: { type: 'number', minimum: 0, maximum: 100 },
        plantingRate: { type: 'number' },
        plantingRateUnit: { enum: [...CASH_CROP_PLANTING_RATE_UNIT] },
        herbicideBurndown: { type: 'boolean' },
        fireBurndown: { type: 'boolean' },
        cropFailure: { type: 'boolean' },
        lossAt: {
          type: ['null', 'string'],
          format: 'date',
          dateAfterField: 'plantedAt',
          dateBeforeField: 'harvestedAt',
        },
        lossPercentage: { type: 'number', minimum: 0, maximum: 100 },
      },
      allOf: [
        {
          if: {
            properties: {
              crop: { enum: ['fallow', 'winterFallow', 'preventPlant'] },
            },
            required: ['crop'],
          },
          then: {
            if: {
              properties: {
                crop: { const: 'preventPlant' },
              },
              required: ['preventPlant'],
            },
            then: {
              required: ['intendedCrop'],
            },
          },
          else: {
            required: ['crop', ...requiredProps],
            if: {
              not: { properties: { harvestType: { enum: ['grazed', 'none', 'not_available'] } } },
              required: ['harvestType'],
            },
            then: {
              required: isDetailRequired({ requirement, fieldName: 'yieldPerAcre' })
                ? ['yieldPerAcre']
                : [],
            },
          },
        },
        {
          if: {
            properties: {
              cropFailure: { const: true },
            },
            // ℹ️ Remember this: A property being checked in 'if' must also be listed as 'required'
            // or the if schema will simply pass by default.
            required: ['cropFailure'],
          },
          then: {
            properties: {
              lossAt: { type: 'string', format: 'date' },
            },
            required: ['lossAt'],
          },
        },
      ] as JSONSchema7WithErrors[],
      required: ['crop'],
    }
  },

  warningJsonSchema: ({ t }) =>
    ({
      type: 'object',
      allOf: [
        {
          $comment: 'bushelsPerAcre',
          if: {
            properties: {
              harvestType: {
                enum: ['grain', 'seed'],
              },
            },
          },
          then: {
            properties: {
              yieldPerAcre: {
                minimum: 10,
                maximum: 350,
                errorMessage: {
                  minimum: t('outsideExpectedRange'),
                  maximum: t('outsideExpectedRange'),
                },
              },
            },
          },
        },
        {
          $comment: 'tonsPerAcre',
          if: {
            properties: {
              harvestType: {
                enum: ['bale', 'silage', 'haylage', 'fruit', 'tuber', 'root'],
              },
            },
          },
          then: {
            properties: {
              yieldPerAcre: {
                minimum: 0.2,
                maximum: 1,
                errorMessage: {
                  minimum: t('outsideExpectedRange'),
                  maximum: t('outsideExpectedRange'),
                },
              },
            },
          },
        },
      ],
    } as unknown as JSONSchema7WithErrors),

  initialValues: request => {
    const detail = findInitialValuesDetail('cashCrop')(request)

    if (!detail) {
      return {
        plantedAt: null,
        harvestedAt: null,
      }
    }

    if (!detail.input) {
      // @ts-ignore MUI's DatePicker prefers empty string to undefined
      return {
        crop: detail.result,
        plantedAt: null,
        harvestedAt: null,
        source: detail.source,
      } as RDCashCrop['input']
    }

    if (!detail.input.crop) {
      // becasue of how cropnosis returns results, it is possible that we have planting and harvest dates
      // but not a crop value. this prefills crop value with the result, then ensures the dates are present
      // then spreads the input value into the form to cover all our bases
      return {
        crop: detail.result,
        plantedAt: null,
        harvestedAt: null,
        source: detail.source,
        cropFailure: !!(detail.input.lossAt || detail.input.lossPercentage),
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...applyDateTransforms(CashCropInput.validJsonSchema!({ t: () => '' }), detail.input),
      }
    }

    const cropValueWithPreventPlant = detail.input.preventPlant
      ? ({ crop: 'preventPlant', intendedCrop: detail.input.crop } as Partial<RDCashCropInput>)
      : { crop: detail.input.crop }

    return {
      plantedAt: null,
      harvestedAt: null,
      source: detail.source,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      ...applyDateTransforms(CashCropInput.validJsonSchema!({ t: () => '' }), detail.input),
      cropFailure: !!(detail?.input.lossAt || detail?.input.lossPercentage),
      ...cropValueWithPreventPlant,
    }
  },

  detailFromEditorValues: (formValues, requirement) => {
    const { source, cropFailure, intendedCrop, lossAt, lossPercentage, ...inputRest } =
      formValues.input
    const lossDetail = cropFailure ? { lossAt, lossPercentage } : {}
    const input =
      inputRest.crop === 'preventPlant'
        ? { ...inputRest, crop: intendedCrop, preventPlant: true }
        : { ...inputRest, ...lossDetail }

    if (isFallow(input.crop)) {
      delete input.plantedAt
      delete input.harvestedAt
      delete input.plantingRate
      delete input.plantingRateUnit
      delete input.harvestType
      delete input.yieldPerAcre
      delete input.residuePercentageRemoved
      delete input.herbicideBurndown
      delete input.fireBurndown
      delete input.lossAt
      delete input.lossPercentage
    }

    return {
      traitId: TRAIT_ID,
      year: requirement?.year,
      input,
    }
  },
}
